import React, { FC } from 'react';

import chunk from 'lodash/chunk';

import { BundleOut } from '../../store/api';
import { TileSlider } from '../slider/TileSlider';
import { ProductMetaData } from '../../../models/Product';
import { concreteLoanFormatNameToKey } from '../../store/loanFormat/actions';
import classNames from 'classnames';
import ProductCover from '../common/ProductCover';
import ProductAuthors from '../product/ProductAuthors';

type Props = {
    readonly bundle: BundleOut;
    readonly shopLanguage: string | undefined;
};

export const BundlePreview: FC<Props> = ({ bundle, shopLanguage }) => {
    const { loanFormat, productAmount = 0, previewProducts: products = [] } = bundle;
    const loanFormatKey = concreteLoanFormatNameToKey(loanFormat);
    const isEaudiobooks = loanFormat?.toLowerCase() === 'eaudiobooks';

    const productAmountThreshold = isEaudiobooks ? 75 : 50;
    const thresholdExceeded = productAmount > productAmountThreshold;

    const itemsPerColumn = thresholdExceeded ? (isEaudiobooks ? 3 : 2) : 1;
    const columns = chunk(products, itemsPerColumn);

    return (
        <TileSlider className="bndl-preview-slider" itemWidth={thresholdExceeded ? 110 : 150} itemHeight={320}>
            {columns.map((column, index) => (
                <Column
                    key={index}
                    products={column as ReadonlyArray<ProductMetaData>}
                    loanFormatKey={loanFormatKey}
                    shopLanguage={shopLanguage}
                />
            ))}
        </TileSlider>
    );
};

type ColumnProps = {
    readonly products: ReadonlyArray<ProductMetaData>;
    readonly loanFormatKey: string;
    readonly shopLanguage: string | undefined;
};

const Column: FC<ColumnProps> = ({ products, loanFormatKey, shopLanguage }) => {
    const many = products.length > 1;
    const isEaudiobooks = loanFormatKey?.toLocaleLowerCase() === 'eaudiobooks';
    const previewClasses = classNames(
        isEaudiobooks ? 'cover-preview-eaudiobook' : 'cover-preview-ebook',
        many ? 'many' : 'few'
    );

    return (
        <div className="slider-column">
            {products.map(product => (
                <div key={product.id} className={previewClasses}>
                    <ProductCover minSizeInPx={many ? 200 : 260} product={product} />
                    {!many && (
                        <div className="details">
                            <p>{product.title}</p>
                            <ProductAuthors
                                product={product}
                                linked={false}
                                loanFormatKey={loanFormatKey}
                                shopLanguage={shopLanguage}
                            />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};
